import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plug, CheckCircle2, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

const CAT_COLORS = { Messaging: "#00E5FF", Payments: "#F97316", Workflows: "#84CC16", Calendar: "#A78BFA" };

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [creds, setCreds] = useState({});

  const load = () => api.get("/integrations").then((r) => setItems(r.data.integrations));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/integrations/config", { integration_id: dlg.id, credentials: creds });
      toast.success(`${dlg.name} conectada`);
      setDlg(null); setCreds({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo");
    }
  };

  const disconnect = async (id) => {
    if (!window.confirm("¿Desconectar? Las tools volverán a modo MOCKED.")) return;
    await api.delete(`/integrations/${id}`);
    toast.success("Desconectada");
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="integrations-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">SETTINGS · CONEXIONES EXTERNAS</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Integraciones</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Al conectar una integración, sus herramientas dejan de estar MOCKED y ejecutan real contra el proveedor.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((i) => (
          <div key={i.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`integ-${i.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 border border-[var(--border)] flex items-center justify-center" style={{ color: CAT_COLORS[i.category] || "#94A3B8" }}>
                <Plug className="w-5 h-5" />
              </div>
              {i.configured ? (
                <Badge className="rounded-none font-mono text-[10px] bg-[var(--good)] text-[var(--bg)] hover:bg-[var(--good)]">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> ACTIVA
                </Badge>
              ) : (
                <Badge className="rounded-none font-mono text-[10px] bg-[var(--surface-2)] text-white hover:bg-[var(--surface-2)] border border-[var(--border)]">
                  MOCKED
                </Badge>
              )}
            </div>
            <div className="font-display font-bold text-lg">{i.name}</div>
            <div className="label-mono mb-3">{i.category}</div>
            <div className="text-xs text-[var(--text-dim)] mb-3">
              Tools activadas: <span className="font-mono text-[var(--accent)]">{i.tools.join(", ")}</span>
            </div>
            {i.configured && (
              <div className="mb-3 text-[10px] font-mono text-[var(--text-dim)] space-y-0.5">
                {Object.entries(i.masked_credentials).filter(([k]) => !k.startsWith("_")).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <Lock className="w-2.5 h-2.5" /> {k}: <span className="text-white">{v}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={() => { setDlg(i); setCreds({}); }}
                className="flex-1 rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80"
                data-testid={`integ-connect-${i.id}`}
              >
                {i.configured ? "Reconfigurar" : "Conectar"}
              </Button>
              {i.configured && (
                <Button onClick={() => disconnect(i.id)} variant="outline"
                        className="rounded-none border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)]/10"
                        data-testid={`integ-disc-${i.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
          {dlg && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Conectar {dlg.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {dlg.fields.map((f) => (
                  <div key={f}>
                    <Label className="label-mono">{f}</Label>
                    <Input
                      type={f.includes("secret") || f.includes("token") || f.includes("key") ? "password" : "text"}
                      value={creds[f] || ""}
                      onChange={(e) => setCreds({ ...creds, [f]: e.target.value })}
                      className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono"
                      data-testid={`integ-field-${f}`}
                    />
                  </div>
                ))}
                <div className="border border-[var(--warn)] bg-[var(--warn)]/5 p-3 text-[11px] text-white leading-relaxed">
                  Las credenciales se guardan cifradas por tenant. Nunca se muestran en claro después.
                  Solo owner/admin pueden ver o editar esta configuración.
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="integ-save">
                  Guardar y conectar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
